import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Link, LoaderFunction, redirect, useLoaderData } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { getPromotions } from "api/methods";
import { Icon } from "atoms/icon/Icon";
import Right from "atoms/icon/icons/chevron_right.svg";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { getPromotionCardImageUrl, getPromotionCardTitle } from "utils/promotionPresentation";

import { getAvailableOffers, getRedeemedOffers, hasAnyOffersToShow } from "./utils";

const load = async () => {
  const promotions = queryClient.fetchQuery({
    ...getPromotions.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  return waitObj({
    promotions,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletOffersLoader: LoaderFunction = async () => {
  const data = await load();
  // Only show page if there are available or redeemed offers to display
  if (!hasAnyOffersToShow(data.promotions)) return redirect("..");
  return data;
};

const WalletOffersPage = () => {
  const { t } = useTranslation();
  const data = useLoaderData() as LoaderData;
  const { data: promotions } = useQuery({
    ...getPromotions.keyed(),
    initialData: data.promotions,
  });

  // Get available and redeemed offers separately
  const availableOffers = getAvailableOffers(promotions);
  const redeemedOffers = getRedeemedOffers(promotions);

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      {availableOffers.length > 0 && (
        <OffersList
          title={t("walletOffersPage.availableOffers")}
          cards={availableOffers.map((p) => ({
            to: `/discover?promotion=${p.id}&from=wallet`,
            imageUrl: getPromotionCardImageUrl(p),
            title: getPromotionCardTitle(p),
            disclaimer: p.disclaimer,
            tag: p.expiresOn
              ? { alert: t("walletOffersPage.expires", { time: dayjs(p.expiresOn).fromNow() }) }
              : null,
          }))}
        />
      )}
      {redeemedOffers.length > 0 && (
        <OffersList
          title={t("walletOffersPage.redeemedOffers")}
          cards={redeemedOffers.map((p) => ({
            to: p.id,
            imageUrl: getPromotionCardImageUrl(p),
            title: getPromotionCardTitle(p),
            disclaimer: p.disclaimer,
            tag: p.redeemedOn
              ? {
                  primary: t("walletOffersPage.redeemed", {
                    time: dayjs(p.redeemedOn).fromNow(),
                  }),
                }
              : null,
          }))}
        />
      )}
    </div>
  );
};

export default WalletOffersPage;

type Tag = { alert: string } | { primary: string };
type TreatmentListProps = {
  title?: string | null;
  cards: {
    to: string;
    title: string;
    disclaimer?: string | null;
    tag?: Tag | null;
    imageUrl?: string | null;
  }[];
};

// TODO: Update the TreatmentList component to allow using it for both
// treatments and offers
const OffersList = (props: TreatmentListProps) => {
  const tag = (t?: Tag | null) => {
    if (!t) return null;

    const type = "alert" in t ? "alert" : "primary";
    const text = "alert" in t ? t.alert : t.primary;

    return (
      <DescriptiveTag size="small" color={type}>
        {text}
      </DescriptiveTag>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {props.title && (
        <h1 className="text-center text-sub2 uppercase text-secondary">{props.title}</h1>
      )}
      <ul className="divide-y divide-background-two overflow-clip rounded-lg shadow-subtle">
        {props.cards.map((card) => (
          <li key={card.to}>
            <Link to={card.to} className="flex items-center gap-3 bg-white p-2">
              {card.imageUrl && (
                <img
                  alt={card.title}
                  className="aspect-square h-24 w-24 rounded object-cover"
                  src={card.imageUrl}
                />
              )}
              <div className="flex grow flex-col text-primary">
                <div className="py-2">{tag(card.tag)}</div>
                <div className="flex flex-col gap-0.5">
                  <div className="line-clamp-2 text-bold1">{card.title}</div>
                  <div className="line-clamp-3 text-body3 italic text-secondary">
                    {card.disclaimer}
                  </div>
                </div>
              </div>
              <div className="mr-2 self-center">
                <Icon svg={Right} size="xsmall" />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
